import { APIErrorException, isAPIError, readServiceError } from "@/core/services";
import type { IHttpClient } from "@/core/interfaces";
import {
  extractItems,
  extractTotal,
  isRecord,
  readFirstText,
  toPaginatedQuery,
} from "@/core/utils";
import { Registry } from "@/shared/infra/dependency-injection/registry";

/** Caminhos consumidos do hub-bff (HU 2225 / 2221). */
export const SIMULACAO_PATHS = {
  BASE: "/api/recursos/simulacoes",
  ATIVA: "/api/recursos/simulacoes/ativa",
  INSTRUCAO: (id: string, instrucaoId: string) =>
    `/api/recursos/simulacoes/${encodeURIComponent(id)}/instrucoes/${encodeURIComponent(instrucaoId)}`,
  DESCARTE: (id: string) => `/api/recursos/simulacoes/${encodeURIComponent(id)}/descarte`,
  ARQUIVAMENTO: (id: string) => `/api/recursos/simulacoes/${encodeURIComponent(id)}/arquivamento`,
  APLICACAO: (id: string) => `/api/recursos/simulacoes/${encodeURIComponent(id)}/aplicacao`,
  DETALHE: (id: string) => `/api/recursos/simulacoes/${encodeURIComponent(id)}`,
} as const;

function getHttpClient(): IHttpClient {
  return Registry.getInstance().inject<IHttpClient>("HttpClient");
}

const ERRO_RASCUNHO_NAO_ABERTO =
  "O rascunho não ficou disponível após abrir a simulação. Recarregue a página e tente novamente.";

function unwrap(responseData: unknown): Record<string, unknown> {
  const body = isRecord(responseData) ? responseData : {};
  return isRecord(body.data) ? body.data : body;
}

function readNumber(raw: Record<string, unknown>, key: string): number {
  const val = raw[key];
  if (typeof val === "number") return val;
  const parsed = Number(val);
  return Number.isFinite(parsed) ? parsed : 0;
}

// ---------------------------------------------------------------------------
// Tipos públicos
// ---------------------------------------------------------------------------

/** Divergência detectada pela API ao carregar a simulação ativa. */
export interface DivergenciaSimulacaoApi {
  tipo: string;
  descricao: string;
}

/** Item de instrução gravado na simulação. */
export interface ItemSimulacaoApi {
  id: string;
  operacao: string;
  tipo: string;
  obraId: string;
  obraCodigo: string;
  obraNome: string;
  payload: Record<string, unknown>;
  anterior: Record<string, unknown> | null;
  conflitosPrevistos: unknown;
}

/** Cabeçalho de uma simulação (retornado no POST e como parte do GET /ativa). */
export interface SimulacaoCabecalhoApi {
  id: string;
  status: string;
  versao: number;
  qtdAlteracoes: number;
  snapshotAlocacao: unknown;
}

/** Simulação ativa completa (GET /ativa). */
export interface SimulacaoAtivaApi extends SimulacaoCabecalhoApi {
  divergencias: DivergenciaSimulacaoApi[];
  itens: ItemSimulacaoApi[];
}

/** Relatório devolvido pelo POST /aplicacao. */
export interface RelatorioAplicacaoApi {
  simulacaoId: string;
  status: string;
  aplicadaEm: string;
  totais: Record<string, unknown>;
  itens: unknown[];
}

/** Totais persistidos no cabeçalho após Aplicar. */
export interface RelatorioTotaisApi {
  instrucoes: number;
  aplicadas: number;
  comAviso: number;
  naoAplicadas: number;
}

export type StatusHistoricoSimulacaoApi =
  "APLICADA" | "APLICADA_PARCIAL" | "DESCARTADA" | "ARQUIVADA";

/** Linha do GET /simulacoes (histórico paginado). */
export interface SimulacaoResumoApi {
  id: string;
  usuarioId: string;
  usuarioNome: string | null;
  status: StatusHistoricoSimulacaoApi | string;
  versao: number;
  qtdAlteracoes: number;
  justificativa: string | null;
  aplicadaEm: string | null;
  createdAt: string;
  relatorioTotais: RelatorioTotaisApi | null;
}

/** GET /simulacoes/{id}: registro completo + relatório reconstruído. */
export interface SimulacaoDetalheApi extends SimulacaoAtivaApi {
  usuarioId: string;
  justificativa: string | null;
  aplicadaEm: string | null;
  createdAt: string;
  relatorioTotais: RelatorioTotaisApi | null;
  relatorio: RelatorioAplicacaoApi | null;
}

// ---------------------------------------------------------------------------
// Leitores auxiliares
// ---------------------------------------------------------------------------

function readDivergencia(raw: unknown): DivergenciaSimulacaoApi | null {
  if (!isRecord(raw)) return null;
  return {
    tipo: readFirstText(raw, "tipo").trim(),
    descricao: readFirstText(raw, "descricao").trim(),
  };
}

function readItemApi(raw: unknown): ItemSimulacaoApi | null {
  if (!isRecord(raw)) return null;
  const id = readFirstText(raw, "id").trim();
  if (!id) return null;
  return {
    id,
    operacao: readFirstText(raw, "operacao").trim(),
    tipo: readFirstText(raw, "tipo").trim(),
    obraId: readFirstText(raw, "obraId").trim(),
    obraCodigo: readFirstText(raw, "obraCodigo").trim(),
    obraNome: readFirstText(raw, "obraNome").trim(),
    payload: isRecord(raw.payload) ? raw.payload : {},
    anterior: isRecord(raw.anterior) ? raw.anterior : null,
    conflitosPrevistos: raw.conflitosPrevistos ?? null,
  };
}

function readCabecalho(data: Record<string, unknown>): SimulacaoCabecalhoApi {
  return {
    id: readFirstText(data, "id").trim(),
    status: readFirstText(data, "status").trim().toUpperCase(),
    versao: readNumber(data, "versao"),
    qtdAlteracoes: readNumber(data, "qtdAlteracoes"),
    snapshotAlocacao: data.snapshotAlocacao ?? null,
  };
}

function readRelatorioTotais(raw: unknown): RelatorioTotaisApi | null {
  if (!isRecord(raw)) return null;
  return {
    instrucoes: readNumber(raw, "instrucoes"),
    aplicadas: readNumber(raw, "aplicadas"),
    comAviso: readNumber(raw, "comAviso"),
    naoAplicadas: readNumber(raw, "naoAplicadas"),
  };
}

function readRelatorioApi(raw: unknown, simulacaoIdFallback: string): RelatorioAplicacaoApi | null {
  if (!isRecord(raw)) return null;
  const status = readFirstText(raw, "status").trim();
  if (!status) return null;
  return {
    simulacaoId: readFirstText(raw, "simulacaoId").trim() || simulacaoIdFallback,
    status,
    aplicadaEm: readFirstText(raw, "aplicadaEm").trim(),
    totais: isRecord(raw.totais) ? raw.totais : {},
    itens: Array.isArray(raw.itens) ? raw.itens : [],
  };
}

function readResumoApi(raw: unknown): SimulacaoResumoApi | null {
  if (!isRecord(raw)) return null;
  const id = readFirstText(raw, "id").trim();
  if (!id) return null;
  const justificativaBruta = readFirstText(raw, "justificativa").trim();
  const aplicadaEmBruta = readFirstText(raw, "aplicadaEm").trim();
  const usuarioNomeBruto = readFirstText(raw, "usuarioNome").trim();
  return {
    id,
    usuarioId: readFirstText(raw, "usuarioId").trim().toLowerCase(),
    usuarioNome: usuarioNomeBruto || null,
    status: readFirstText(raw, "status").trim().toUpperCase(),
    versao: readNumber(raw, "versao"),
    qtdAlteracoes: readNumber(raw, "qtdAlteracoes"),
    justificativa: justificativaBruta || null,
    aplicadaEm: aplicadaEmBruta || null,
    createdAt: readFirstText(raw, "createdAt").trim(),
    relatorioTotais: readRelatorioTotais(raw.relatorioTotais),
  };
}

/** Monta a view de simulação ativa a partir do corpo já desembrulhado da API. */
export function parseSimulacaoAtivaApi(data: Record<string, unknown>): SimulacaoAtivaApi | null {
  if (!readFirstText(data, "id").trim()) return null;
  const cabecalho = readCabecalho(data);
  return {
    ...cabecalho,
    divergencias: extractItems(data.divergencias)
      .map(readDivergencia)
      .filter((d): d is DivergenciaSimulacaoApi => d !== null),
    itens: extractItems(data.itens)
      .map(readItemApi)
      .filter((i): i is ItemSimulacaoApi => i !== null),
  };
}

/** Monta o detalhe de histórico (GET /{id}). */
export function parseSimulacaoDetalheApi(
  data: Record<string, unknown>,
): SimulacaoDetalheApi | null {
  const ativa = parseSimulacaoAtivaApi(data);
  if (!ativa) return null;
  const justificativaBruta = readFirstText(data, "justificativa").trim();
  const aplicadaEmBruta = readFirstText(data, "aplicadaEm").trim();
  return {
    ...ativa,
    usuarioId: readFirstText(data, "usuarioId").trim(),
    justificativa: justificativaBruta || null,
    aplicadaEm: aplicadaEmBruta || null,
    createdAt: readFirstText(data, "createdAt").trim(),
    relatorioTotais: readRelatorioTotais(data.relatorioTotais),
    relatorio: readRelatorioApi(data.relatorio, ativa.id),
  };
}

// ---------------------------------------------------------------------------
// Funções exportadas
// ---------------------------------------------------------------------------

/**
 * GET /api/recursos/simulacoes/ativa
 * Retorna null quando 204 (nenhuma simulação em aberto).
 */
export async function buscarSimulacaoAtiva(
  signal?: AbortSignal,
): Promise<SimulacaoAtivaApi | null> {
  const response = await getHttpClient().get<unknown>(SIMULACAO_PATHS.ATIVA, { signal });
  if (response.status === 204 || !response.data) return null;
  return parseSimulacaoAtivaApi(unwrap(response.data));
}

/**
 * POST /api/recursos/simulacoes → confirma com GET /ativa (fonte de verdade).
 * Se 409 SIMULACAO_ATIVA_EXISTENTE, reconsulta /ativa.
 */
export async function criarSimulacao(signal?: AbortSignal): Promise<SimulacaoAtivaApi> {
  try {
    await getHttpClient().post<unknown>(SIMULACAO_PATHS.BASE, {}, { signal });
  } catch (error) {
    if (isAPIError(error) && error.status === 409) {
      const { code } = readServiceError(error);
      if (code === "SIMULACAO_ATIVA_EXISTENTE") {
        const ativa = await buscarSimulacaoAtiva(signal);
        if (ativa?.status === "ATIVA") return ativa;
      }
    }
    throw error;
  }

  const confirmada = await buscarSimulacaoAtiva(signal);
  if (confirmada?.status === "ATIVA") return confirmada;

  throw new APIErrorException(503, ERRO_RASCUNHO_NAO_ABERTO, ERRO_RASCUNHO_NAO_ABERTO);
}

/** Instrução da simulação: a operação e o corpo do endpoint real, mais o que só a simulação guarda. */
export interface InstrucaoPayload {
  operacao: string;
  tipo: string;
  obraId: string;
  obraCodigo: string;
  obraNome: string;
  payload: Record<string, unknown>;
  anterior: Record<string, unknown>;
  conflitosPrevistos?: unknown;
  /** Período da equipe: o gravado no core, para reenviar a alocação que voltou a ele. Não vai no corpo. */
  periodosOriginais?: { alocacaoId: string; inicio: string; fim: string }[];
}

/** Chamada a um endpoint real de Recursos com `simular: true`. */
export interface RequisicaoInstrucao {
  method: "post" | "patch";
  path: string;
  body: Record<string, unknown>;
}

const ALOCACOES = "/api/recursos/alocacoes";
const segmento = (valor: unknown) => encodeURIComponent(String(valor ?? ""));

function periodosDoPayload(payload: Record<string, unknown>): Record<string, unknown>[] {
  if (Array.isArray(payload.alocacoes)) return payload.alocacoes.filter(isRecord);
  return typeof payload.alocacaoId === "string" ? [payload] : [];
}

/**
 * Endpoint real de cada operação (o mesmo da alocação de fato) e o corpo com `simular: true`. O
 * período da equipe é uma chamada por alocação: o core junta todas na mesma instrução da obra.
 */
export function requisicoesDaInstrucao(instrucao: InstrucaoPayload): RequisicaoInstrucao[] {
  const simulacao = {
    anterior: instrucao.anterior,
    conflitosPrevistos: Array.isArray(instrucao.conflitosPrevistos)
      ? instrucao.conflitosPrevistos
      : [],
  };
  const comSimulacao = (body: Record<string, unknown>) => ({ ...body, simular: true, simulacao });
  const { alocacaoId, ...resto } = instrucao.payload;
  const obra = segmento(instrucao.obraId);

  switch (instrucao.operacao) {
    case "definir-equipe":
      return [
        {
          method: "post",
          path: `${ALOCACOES}/obras/${obra}/profissionais`,
          body: comSimulacao(instrucao.payload),
        },
      ];
    case "editar-periodo-equipe":
      return periodosDoPayload(instrucao.payload).map((periodo) => ({
        method: "patch",
        path: `${ALOCACOES}/obras/${obra}/profissionais/${segmento(periodo.alocacaoId)}`,
        body: comSimulacao({ inicio: periodo.inicio, fim: periodo.fim }),
      }));
    case "remanejar-profissional":
      return [
        {
          method: "patch",
          path: `${ALOCACOES}/profissionais/${segmento(alocacaoId)}/remanejamento`,
          body: comSimulacao(resto),
        },
      ];
    case "remanejar-local":
      return [
        {
          method: "patch",
          path: `${ALOCACOES}/locais/${segmento(alocacaoId)}/remanejamento`,
          body: comSimulacao(resto),
        },
      ];
    case "remanejar-fornecedor":
      return [
        {
          method: "patch",
          path: `${ALOCACOES}/fornecedores/${segmento(alocacaoId)}/remanejamento`,
          body: comSimulacao(resto),
        },
      ];
    case "ignorar-conflito-profissional":
      return [
        {
          method: "post",
          path: `${ALOCACOES}/profissionais/conflitos-ignorados`,
          body: comSimulacao(instrucao.payload),
        },
      ];
    case "ignorar-conflito-local":
      return [
        {
          method: "post",
          path: `${ALOCACOES}/locais/conflitos-ignorados`,
          body: comSimulacao(instrucao.payload),
        },
      ];
    case "ignorar-conflito-fornecedor":
      return [
        {
          method: "post",
          path: `${ALOCACOES}/fornecedores/conflitos-ignorados`,
          body: comSimulacao(instrucao.payload),
        },
      ];
    default:
      throw new Error(`Operação de simulação sem endpoint: ${instrucao.operacao}`);
  }
}

/**
 * O core soma os períodos à instrução gravada da obra e tira dela a alocação que volta ao período
 * real. A que estava gravada e não mudou mais é reenviada com o período original; nada é apagado
 * antes, então uma falha no meio não perde a edição anterior.
 */
async function comPeriodosRevertidos(
  instrucao: InstrucaoPayload,
  signal?: AbortSignal,
): Promise<InstrucaoPayload> {
  const originais = instrucao.periodosOriginais ?? [];
  if (originais.length === 0) return instrucao;
  const ativa = await buscarSimulacaoAtiva(signal);
  const gravada = ativa?.itens.find(
    (item) =>
      item.operacao === "editar-periodo-equipe" &&
      item.obraId.toLowerCase() === instrucao.obraId.toLowerCase(),
  );
  if (!gravada) return instrucao;
  const enviados = periodosDoPayload(instrucao.payload);
  const idsEnviados = new Set(enviados.map((periodo) => String(periodo.alocacaoId).toLowerCase()));
  const idsGravados = new Set(
    periodosDoPayload(gravada.payload).map((periodo) => String(periodo.alocacaoId).toLowerCase()),
  );
  const revertidos = originais.filter((original) => {
    const id = original.alocacaoId.toLowerCase();
    return idsGravados.has(id) && !idsEnviados.has(id);
  });
  return { ...instrucao, payload: { alocacoes: [...enviados, ...revertidos] } };
}

/**
 * Uma instrução de várias requisições falhou depois de gravar parte delas: o core já tem a parte
 * gravada. `cause` é o erro da requisição que falhou.
 */
export class InstrucaoGravadaEmParte extends Error {
  constructor(cause: unknown) {
    super("A instrução foi gravada em parte na simulação.", { cause });
    this.name = "InstrucaoGravadaEmParte";
  }
}

/**
 * Grava a instrução na simulação ativa pelos endpoints reais de Recursos com `simular: true`.
 * Retorna o item criado/substituído. Falha depois de uma requisição gravada lança
 * `InstrucaoGravadaEmParte`.
 */
export async function adicionarInstrucao(
  _simulacaoId: string,
  instrucao: InstrucaoPayload,
  signal?: AbortSignal,
): Promise<ItemSimulacaoApi | null> {
  const envio =
    instrucao.operacao === "editar-periodo-equipe"
      ? await comPeriodosRevertidos(instrucao, signal)
      : instrucao;
  const http = getHttpClient();
  let item: ItemSimulacaoApi | null = null;
  let gravadas = 0;
  for (const requisicao of requisicoesDaInstrucao(envio)) {
    try {
      const response =
        requisicao.method === "post"
          ? await http.post<unknown>(requisicao.path, requisicao.body, { signal })
          : await http.patch<unknown>(requisicao.path, requisicao.body, { signal });
      item = readItemApi(unwrap(response.data));
    } catch (error) {
      throw gravadas > 0 ? new InstrucaoGravadaEmParte(error) : error;
    }
    gravadas += 1;
  }
  return item;
}

/**
 * DELETE /api/recursos/simulacoes/{id}/instrucoes/{instrucaoId} → 204.
 */
export async function removerInstrucao(
  simulacaoId: string,
  instrucaoId: string,
  signal?: AbortSignal,
): Promise<void> {
  await getHttpClient().delete<unknown>(SIMULACAO_PATHS.INSTRUCAO(simulacaoId, instrucaoId), {
    signal,
  });
}

/**
 * POST /api/recursos/simulacoes/{id}/descarte com If-Match: versao → 200.
 */
/** POST /arquivamento com If-Match. Encerra o rascunho que já tem alterações. */
export async function arquivarSimulacao(
  simulacaoId: string,
  versao: number,
  signal?: AbortSignal,
): Promise<void> {
  await getHttpClient().post<unknown>(
    SIMULACAO_PATHS.ARQUIVAMENTO(simulacaoId),
    {},
    { signal, headers: { "If-Match": String(versao) } },
  );
}

export async function descartarSimulacao(
  simulacaoId: string,
  versao: number,
  signal?: AbortSignal,
): Promise<void> {
  await getHttpClient().post<unknown>(
    SIMULACAO_PATHS.DESCARTE(simulacaoId),
    {},
    { signal, headers: { "If-Match": String(versao) } },
  );
}

/**
 * POST /api/recursos/simulacoes/{id}/aplicacao → 200 relatório.
 */
export async function aplicarSimulacao(
  simulacaoId: string,
  versao: number,
  justificativa: string,
  signal?: AbortSignal,
): Promise<RelatorioAplicacaoApi> {
  const response = await getHttpClient().post<unknown>(
    SIMULACAO_PATHS.APLICACAO(simulacaoId),
    { justificativa },
    { signal, headers: { "If-Match": String(versao) } },
  );
  const data = unwrap(response.data);
  return {
    simulacaoId: readFirstText(data, "simulacaoId").trim() || simulacaoId,
    status: readFirstText(data, "status").trim(),
    aplicadaEm: readFirstText(data, "aplicadaEm").trim(),
    totais: isRecord(data.totais) ? data.totais : {},
    itens: Array.isArray(data.itens) ? data.itens : [],
  };
}

/**
 * GET /api/recursos/simulacoes?page&pageSize&status → histórico paginado.
 */
export async function listarHistoricoSimulacoes(
  params: { page?: number; pageSize?: number; status?: string },
  signal?: AbortSignal,
): Promise<{ itens: SimulacaoResumoApi[]; total: number }> {
  const query: Record<string, string | number> = { ...toPaginatedQuery(params) };
  const statusBruto = params.status;
  const status = typeof statusBruto === "string" ? statusBruto.trim() : "";
  if (status) query.status = status;
  const response = await getHttpClient().get<unknown>(SIMULACAO_PATHS.BASE, {
    signal,
    params: query,
  });
  const body = isRecord(response.data) ? response.data : {};
  const itens = extractItems(body)
    .map(readResumoApi)
    .filter((item): item is SimulacaoResumoApi => item !== null);
  const total = extractTotal(body) ?? itens.length;
  return { itens, total };
}

/**
 * GET /api/recursos/simulacoes/{id} → detalhe + relatório.
 */
export async function buscarDetalheSimulacao(
  id: string,
  signal?: AbortSignal,
): Promise<SimulacaoDetalheApi> {
  const response = await getHttpClient().get<unknown>(SIMULACAO_PATHS.DETALHE(id), { signal });
  const parsed = parseSimulacaoDetalheApi(unwrap(response.data));
  if (!parsed) {
    throw new APIErrorException(404, "Simulação não encontrada.", "Simulação não encontrada.");
  }
  return parsed;
}
